import moment from 'moment';
import { NetworkService } from '../../services';
import { UtilityConstants } from './types';
import { NetworkActions } from '../network';
import { config } from '../../config';

// API endpoints
const { BASE_URL } = config;

export class UtilityActions {
  static getUtilityOverview(api_location, weatherAdjusted, category = undefined, subcategory = undefined) {
    return async (dispatch, getState) => {
      dispatch({ type: UtilityConstants.RequestUtilityOverview });
      const requestInfo = {
        category,
        subcategory,
      };
      await dispatch(NetworkActions.updateLastRequest(requestInfo));

      const state = getState();
      const { utilityReducer = {} } = state;
      const graphDateRange =
        category === 'electrical-usage' ? utilityReducer.electricalGraphDateRange : utilityReducer.gasGraphDateRange;
      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');
        let urlFormatted = `${BASE_URL}${api_location}`;
        if (graphDateRange) {
          const options = UtilityActions.getUtilityOptions(graphDateRange);
          urlFormatted = UtilityActions.formatUtilityUrl(urlFormatted, options, weatherAdjusted);
        }
        return fetch(urlFormatted, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok) {
        return dispatch({ type: UtilityConstants.SetUtilityError, payload: 'Error setting utility' });
      }

      const data = await response.json();
      const newState = getState();
      const { lastRequest } = newState.networkReducer;
      const { selectedBuilding } = newState.buildingReducer;

      // Make sure we are only handling correct responses
      if (
        (lastRequest.category === undefined && lastRequest.subcategory === undefined) ||
        lastRequest.subcategory === data.name ||
        selectedBuilding.name.includes('demo')
      ) {
        return dispatch({ type: UtilityConstants.ReceiveUtilityOverview, payload: data });
      }
    };
  }

  static updateElectricalGraphDateRange(graphDateRange) {
    window.sessionStorage.setItem('electricalGraphDateRange', JSON.stringify(graphDateRange));
    return { type: UtilityConstants.UpdateElectricalGraphDateRange, payload: graphDateRange };
  }

  static updateGasGraphDateRange(graphDateRange) {
    window.sessionStorage.setItem('gasGraphDateRange', JSON.stringify(graphDateRange));
    return { type: UtilityConstants.UpdateGasGraphDateRange, payload: graphDateRange };
  }

  static formatUtilityUrl(url, { startDatetime, endDatetime, reportUnit }, weatherAdjusted) {
    let weatherAdjustedShortForm = weatherAdjusted ? 'wa' : 'nwa';
    let urlFormatted = url;
    urlFormatted = `${urlFormatted}?report_unit=${reportUnit}`;
    urlFormatted = `${urlFormatted}&end_datetime=${endDatetime}`;
    urlFormatted = `${urlFormatted}&start_datetime=${startDatetime}`;
    urlFormatted = `${urlFormatted}&weather_adjustment=${weatherAdjustedShortForm}`;
    return urlFormatted;
  }

  static nearestPastMinutes(interval, someMoment) {
    return Math.floor(someMoment.minute() / interval) * interval;
  }

  static getUtilityOptions(graphDateRange, categoryName = '') {
    const { value, startDatetime, endDatetime } = graphDateRange;

    if (value === 'custom') {
      // Set the adjusted end date to the most distant past between current time and end of day of endDatetime
      // For example, if end of day of endDatetime is in the future, use current time
      const adjustedEndDatetime = moment.min(moment(), moment(endDatetime).endOf('day'));

      const diff = moment(adjustedEndDatetime).diff(moment(startDatetime).startOf('day'), 'hours', true);
      const options = {
        startDatetime: moment(startDatetime),
        endDatetime: moment(adjustedEndDatetime),
        reportUnit: 'day',
      };

      if (diff < 6 && (categoryName === 'Diagnostics' || categoryName === 'Thermostat')) {
        options.reportUnit = 'minute';
        options.startDatetime = options.startDatetime.startOf('day').utc().format();
        options.endDatetime = moment(options.endDatetime)
          .minute(this.nearestPastMinutes(1, moment(options.endDatetime)))
          .second(0)
          .utc()
          .format();
      } else if (diff < 26) {
        options.reportUnit = 'five';
        options.startDatetime = options.startDatetime.startOf('day').utc().format();
        options.endDatetime = moment(options.endDatetime)
          .minute(this.nearestPastMinutes(5, moment(options.endDatetime)))
          .second(0)
          .utc()
          .format();
      } else if (diff < 74) {
        options.reportUnit = 'fifteen';
        options.startDatetime = options.startDatetime.startOf('day').utc().format();
        options.endDatetime = moment(options.endDatetime)
          .minute(this.nearestPastMinutes(15, moment(options.endDatetime)))
          .second(0)
          .utc()
          .format();
      } else if (diff < 290) {
        options.reportUnit = 'hour';
        options.startDatetime = options.startDatetime.startOf('day').utc().format();
        options.endDatetime = moment(options.endDatetime)
          .minute(this.nearestPastMinutes(60, moment(options.endDatetime)))
          .second(0)
          .utc()
          .format();
      } else {
        options.reportUnit = 'day';
        options.startDatetime = options.startDatetime.startOf('day').utc().format();
        options.endDatetime = options.endDatetime.endOf('day').utc().format();
      }
      return options;
    }

    const options = {
      startDatetime: moment(),
      endDatetime: moment(),
      reportUnit: 'day',
    };

    if (value === '1Hour') {
      options.reportUnit = 'minute';
      options.startDatetime = options.startDatetime
        .minute(this.nearestPastMinutes(1, moment(options.startDatetime)))
        .subtract(1, 'hours')
        .utc();
      options.endDatetime = moment(options.endDatetime)
        .minute(this.nearestPastMinutes(1, moment(options.endDatetime)))
        .second(0)
        .utc();
    } else if (value === '6Hours') {
      options.reportUnit = 'minute';
      options.startDatetime = options.startDatetime
        .minute(this.nearestPastMinutes(1, moment(options.startDatetime)))
        .subtract(6, 'hours')
        .utc();
      options.endDatetime = moment(options.endDatetime)
        .minute(this.nearestPastMinutes(1, moment(options.endDatetime)))
        .second(0)
        .utc();
    } else if (
      (value === '24Hours' || value === '48Hours') &&
      (categoryName === 'Diagnostics' || categoryName === 'Thermostat')
    ) {
      options.reportUnit = 'fifteen_minutes';
      if (value === '24Hours') {
        options.startDatetime = options.startDatetime.startOf('hour').subtract(24, 'hours').utc();
        options.endDatetime = moment(options.endDatetime)
          .minute(this.nearestPastMinutes(5, moment(options.endDatetime)))
          .second(0)
          .utc();
      } else {
        options.startDatetime = options.startDatetime.startOf('hour').subtract(48, 'hours').utc();
        options.endDatetime = moment(options.endDatetime)
          .minute(this.nearestPastMinutes(15, moment(options.endDatetime)))
          .second(0)
          .utc();
      }
    } else if (value === '7Days' || value === 'month') {
      if (value === '7Days') {
        options.startDatetime = options.startDatetime.startOf('day').subtract(7, 'days').utc();
        options.endDatetime = moment(options.endDatetime)
          .minute(this.nearestPastMinutes(15, moment(options.endDatetime)))
          .second(0)
          .utc();
      } else {
        options.startDatetime = options.startDatetime.startOf('month').subtract(1, 'months').utc();
        options.endDatetime = options.endDatetime.endOf('month').subtract(1, 'months').utc();
      }
    } else if (value === '30Days') {
      options.startDatetime = options.startDatetime.startOf('day').subtract(30, 'days').utc();
      options.endDatetime = options.endDatetime.endOf('day').utc();
    } else if (value === '3Months') {
      options.reportUnit = 'day';
      options.endDatetime = options.endDatetime.endOf('day').utc();
      options.startDatetime = options.startDatetime.startOf('day').subtract(3, 'months').utc();
    } else {
      options.reportUnit = 'day';
      options.endDatetime = options.endDatetime.endOf('day').utc();
      options.startDatetime = options.startDatetime.startOf('day').subtract(1, 'years').utc();
    }
    options.endDatetime = options.endDatetime.format();
    options.startDatetime = options.startDatetime.format();

    return options;
  }

  static toggleWeatherAdjusted(toggleValue) {
    return { type: UtilityConstants.UpdateWeatherAdjustedToggle, payload: toggleValue };
  }
}
